import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { FirebaseAlertService, RealtimeAlert } from '../../services/firebase-alert.service';
import { Subscription } from 'rxjs';
import { animate, style, transition, trigger } from '@angular/animations';

@Component({
  selector: 'app-realtime-alert',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule],
  template: `
    <div class="alert-container" *ngIf="currentAlert" @slideIn>
      <div class="alert-content">
        <div class="alert-header">
          <mat-icon class="alert-icon">notifications_active</mat-icon>
          <span class="alert-title">{{ currentAlert.title }}</span>
          <button mat-icon-button (click)="closeAlert()" class="close-btn">
            <mat-icon>close</mat-icon>
          </button>
        </div>
        <div class="alert-body">
          <p>{{ currentAlert.description }}</p>
        </div>
        <div class="alert-footer">
          <button mat-flat-button color="primary" (click)="navigateToUrl()">
            View Details
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .alert-container {
      position: fixed;
      top: 80px;
      right: 20px;
      width: 320px;
      background: var(--mat-sys-surface-container-high);
      border-radius: 12px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
      z-index: 9999;
      border: 1px solid var(--mat-sys-outline-variant);
      overflow: hidden;
    }
    .alert-content {
      padding: 16px;
    }
    .alert-header {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 12px;
    }
    .alert-icon {
      color: var(--mat-sys-primary);
    }
    .alert-title {
      font-weight: 600;
      font-size: 1.1rem;
      flex-grow: 1;
    }
    .close-btn {
      margin-right: -8px;
      margin-top: -8px;
    }
    .alert-body {
      margin-bottom: 16px;
      color: var(--mat-sys-on-surface-variant);
      line-height: 1.5;
    }
    .alert-footer {
      display: flex;
      justify-content: flex-end;
    }
  `],
  animations: [
    trigger('slideIn', [
      transition(':enter', [
        style({ transform: 'translateX(100%)', opacity: 0 }),
        animate('300ms cubic-bezier(0.4, 0, 0.2, 1)', style({ transform: 'translateX(0)', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('200ms cubic-bezier(0.4, 0, 0.2, 1)', style({ transform: 'translateX(100%)', opacity: 0 }))
      ])
    ])
  ]
})
export class RealtimeAlertComponent implements OnInit, OnDestroy {
  private alertService = inject(FirebaseAlertService);
  private router = inject(Router);
  private sub?: Subscription;

  currentAlert: RealtimeAlert | null = null;
  private alertQueue: RealtimeAlert[] = [];
  private autoCloseTimeout: any;

  ngOnInit() {
    this.sub = this.alertService.alerts$.subscribe(alert => {
      this.queueAlert(alert);
    });
  }

  private queueAlert(alert: RealtimeAlert) {
    this.alertQueue.push(alert);
    if (!this.currentAlert) {
      this.processNextAlert();
    }
  }

  private processNextAlert() {
    if (this.alertQueue.length === 0) {
      this.currentAlert = null;
      return;
    }

    const nextAlert = this.alertQueue.shift()!;
    this.currentAlert = nextAlert;

    // ✅ Mark as seen in Firebase immediately after it shows on screen
    if (nextAlert.path && nextAlert.id) {
      this.alertService.markAsSeen(nextAlert.path, nextAlert.id);
    }

    // Auto close after 10 seconds to show the next one in queue
    if (this.autoCloseTimeout) {
      clearTimeout(this.autoCloseTimeout);
    }
    this.autoCloseTimeout = setTimeout(() => {
      this.closeAlert();
    }, 10000);
  }

  closeAlert() {
    this.currentAlert = null;
    if (this.autoCloseTimeout) {
      clearTimeout(this.autoCloseTimeout);
    }
    
    // Brief delay before showing next alert for smooth transition
    setTimeout(() => {
      this.processNextAlert();
    }, 500);
  }

  navigateToUrl() {
    if (this.currentAlert) {
      this.router.navigate([this.currentAlert.url], { 
        queryParams: this.currentAlert.query 
      });
      this.closeAlert();
    }
  }

  ngOnDestroy() {
    this.sub?.unsubscribe();
    if (this.autoCloseTimeout) {
      clearTimeout(this.autoCloseTimeout);
    }
  }
}

